import type * as Sentry from "@sentry/browser";

export type ErrorSource = "spa" | "external";

/**
 * Classifies an error event by the origin of its stack frames.
 *
 * Our WebView hosts can inject scripts into our frame (e.g. a native
 * `WebViewJavascriptBridge`), so their uncaught throws are reported by our
 * global handlers and arrive tagged with our build/session context. Those
 * frames carry no filename from our own bundle.
 *
 * Anything unattributable — no exception, no frames, cross-origin
 * `"Script error."` — stays `"spa"`; missing evidence is not evidence of
 * foreign code.
 */
export function errorSource(
	event: Sentry.ErrorEvent,
	origin: string = window.location.origin,
): ErrorSource {
	const frames = event.exception?.values?.[0]?.stacktrace?.frames;

	// `file:` hosts report an opaque `"null"` origin, which no frame filename can
	// match — treat it as unknown rather than declaring every frame foreign.
	if (!origin || origin === "null" || !frames?.length) {
		return "spa";
	}

	// Frames can arrive without any filename at all (native, minified eval); that
	// is absent evidence, not foreign code.
	if (!frames.some((frame) => frame.filename)) {
		return "spa";
	}

	// The trailing slash keeps a lookalike host (`<origin>.evil.com`) from
	// prefix-matching as ours.
	return frames.some((frame) => frame.filename?.startsWith(`${origin}/`))
		? "spa"
		: "external";
}
